import { useState } from 'react';
import type { ILocation } from '@map-bldr/shared';

export interface IExportModalProps {
  allLocations: ILocation[];
  filteredLocations: ILocation[];
  onClose: () => void;
}

type ExportType = 'all' | 'filtered';

export function ExportModal({ allLocations, filteredLocations, onClose }: IExportModalProps) {
  const [mapName, setMapName] = useState('');
  const [exportType, setExportType] = useState<ExportType>('all');
  const isFilteredSameAsAll = filteredLocations.length === allLocations.length;

  const handleExport = () => {
    if (!mapName.trim()) {
      alert('Please enter a map name');
      return;
    }

    const locationsToExport = exportType === 'all' ? allLocations : filteredLocations;

    const exportData = {
      name: mapName.trim(),
      customCoordinates: locationsToExport.map(loc => ({
        lat: loc.lat,
        lng: loc.lng,
        heading: loc.heading,
        pitch: loc.pitch,
        zoom: loc.zoom,
        ...(loc.pano_id && { panoId: loc.pano_id })
      }))
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${mapName.trim()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    onClose();
  };

  const handleCancel = () => {
    setMapName('');
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleExport();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-xl p-6 w-[90%] max-w-md shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="m-0 mb-4 text-xl font-bold font-mono text-foreground">
          <span className="text-muted-foreground">&gt;</span> Export Map
        </h2>
        <p className="m-0 mb-4 text-muted-foreground text-sm font-mono">
          Enter a name for your map:
        </p>
        <input
          type="text"
          value={mapName}
          onChange={(e) => setMapName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="My Custom Map"
          maxLength={60}
          autoFocus
          className="w-full px-3 py-3 text-sm font-mono border border-input rounded-md mb-4 box-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <div className="mb-4">
          <p className="m-0 mb-2 text-muted-foreground text-sm font-mono">
            Select locations to export:
          </p>
          <div className="flex flex-col gap-2">
            <label className="flex items-center cursor-pointer text-sm font-mono text-foreground">
              <input
                type="radio"
                name="exportType"
                value="all"
                checked={exportType === 'all'}
                onChange={(e) => setExportType(e.target.value as ExportType)}
                className="mr-2 cursor-pointer"
              />
              Export all ({allLocations.length} locations)
            </label>
            <label className={`flex items-center text-sm font-mono ${isFilteredSameAsAll ? 'cursor-not-allowed text-muted-foreground opacity-50' : 'cursor-pointer text-foreground'}`}>
              <input
                type="radio"
                name="exportType"
                value="filtered"
                checked={exportType === 'filtered'}
                onChange={(e) => setExportType(e.target.value as ExportType)}
                disabled={isFilteredSameAsAll}
                className="mr-2 cursor-pointer disabled:cursor-not-allowed"
              />
              Only filtered locations ({filteredLocations.length} locations)
            </label>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={handleCancel}
            className="bg-secondary text-secondary-foreground border-none rounded-md px-5 py-2.5 text-sm font-bold cursor-pointer font-mono hover:opacity-90 transition-opacity"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            className="bg-primary text-primary-foreground border-none rounded-md px-5 py-2.5 text-sm font-bold cursor-pointer font-mono hover:opacity-90 transition-opacity"
          >
            Export →
          </button>
        </div>
      </div>
    </div>
  );
}
